import { forwardRef } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

export type ButtonVariant =
  | "brand"
  | "brand-accent"
  | "neutral"
  | "neutral-plain"
  | "danger"
  | "success"
  | "ghost";

export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  href?: string;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  brand: "btn-brand",
  "brand-accent": "btn-brand-accent",
  neutral: "btn-neutral",
  "neutral-plain": "btn-neutral-plain",
  danger: "btn-danger",
  success: "btn-success",
  ghost: "btn-ghost",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "btn-sm",
  md: "btn-md",
  lg: "btn-lg",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "neutral",
      size = "md",
      href,
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      className,
      ...props
    },
    ref
  ) => {
    const classes = clsx(
      "btn",
      variantClasses[variant],
      sizeClasses[size],
      className
    );

    const content = (
      <>
        {loading ? (
          <span className="spinner spinner-sm" />
        ) : (
          leftIcon
        )}
        {children}
        {rightIcon}
      </>
    );

    if (href && !disabled && !loading) {
      return (
        <Link to={href} className={classes}>
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
